import WooMoments from '@efex-components/common/Modal/WooMoments';
import { Dialog, DialogTitle, DialogContent } from '@efexteam/components';
import { useRole } from '@efexteam/permission-manager';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Button, Typography } from '@mui/material';
import ApproveBeneficiaryComponent from '@pages/approveBeneficiary/ApproveBeneficiaryComponent';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import FormCard from './components/FormCard';
import { registerFormSteps } from './constants';
import useAddContact from './hooks/useAddContact';
import useBuildPending from './hooks/useBuildPending';
import AddressData from './steps/AddressData';
import BankData from './steps/BankData';
import BaseData from './steps/BaseData';
import ConfirmExistingContactInformation from './steps/ConfirmExistingContactInformation';
import Confirmation from './steps/Confirmation';

interface IAddContactForm {
  onClose: () => void;
  open: boolean;
}

const AddContactForm = ({ onClose, open }: IAddContactForm) => {
  const { t } = useTranslation();
  const {
    formStep,
    steps,
    responseExistingLoading,
    responseLoading,
    responseError,
    responseExistingError,
    selectedExistingContact,
    data,
    responseExistingData,
    responseData,
    createNewContact,
    createContactFromExistingContact,
    createFromExist,
    nextFormStep,
    prevFormStep,
    closeAndClearContext,
    setFormStep,
  } = useAddContact({ onClose });

  const { buildPending } = useBuildPending();
  const { isPromoter } = useRole();

  const WooMomentProps = {
    title: (
      <>
        <div>
          <Typography variant="body2" className="!font-light">
            {t(`wooReferral`)}
          </Typography>
          <Typography variant="body2">
            <b>
              {t(`wooReferralAmount`, {
                amount: 200,
                currency: 'USD',
              })}
            </b>
          </Typography>
        </div>
        <Button
          variant="contained"
          size="small"
          className="!text-[#006BF8] !bg-white !border-white !ml-6 !rounded-sm !capitalize"
          onClick={closeAndClearContext}
        >
          <Link to="/clients?action=referral">{t('wooReferralButton')}</Link>
        </Button>
      </>
    ),
    momentType: 'info' as const,
    icon: <CampaignOutlinedIcon fontSize="large" />,
    onClose: closeAndClearContext,
  };

  const isWooMoment = responseExistingData?.id || responseData?.enterprise.id;

  return (
    <Dialog open={open} onClose={closeAndClearContext} fullWidth maxWidth="sm">
      {isWooMoment && WooMomentProps ? (
        <WooMoments {...WooMomentProps} />
      ) : (
        <DialogTitle showCloseButton onClick={closeAndClearContext} />
      )}
      <DialogContent>
        <FormCard
          currentStep={formStep}
          activeStep={formStep}
          setActiveStep={setFormStep}
          steps={steps}
        >
          <>
            {formStep === registerFormSteps.CONTACT_INFORMATION &&
              !selectedExistingContact && (
                <BaseData
                  formStep={formStep}
                  loading={responseExistingLoading}
                  nextFormStep={nextFormStep}
                  prevStep={prevFormStep}
                  createExisting={createFromExist}
                />
              )}
            {formStep === registerFormSteps.ADDRESS_DATA &&
              !selectedExistingContact && (
                <AddressData
                  formStep={formStep}
                  nextFormStep={nextFormStep}
                  prevStep={prevFormStep}
                />
              )}
            {formStep === registerFormSteps.BANK_DATA &&
              !selectedExistingContact && (
                <BankData
                  formStep={formStep}
                  loading={responseLoading}
                  error={responseError}
                  retry={createNewContact}
                  nextFormStep={createNewContact}
                  prevStep={prevFormStep}
                  close={onClose}
                />
              )}
            {formStep === registerFormSteps.CONTACT_INFORMATION &&
              selectedExistingContact && (
                <ConfirmExistingContactInformation
                  formStep={formStep}
                  contact={selectedExistingContact}
                  loading={responseExistingLoading}
                  error={responseExistingError}
                  retry={createContactFromExistingContact}
                  nextFormStep={createContactFromExistingContact}
                  prevStep={prevFormStep}
                />
              )}
            {formStep === registerFormSteps.CONTACT_CONFIRMATION &&
              (isPromoter ? (
                <ApproveBeneficiaryComponent
                  error={false}
                  showRemainingTime
                  action="pending"
                  beneficiaryData={buildPending(data || {})}
                />
              ) : (
                <Confirmation
                  email={data?.email}
                  onClose={closeAndClearContext}
                />
              ))}
          </>
        </FormCard>
      </DialogContent>
    </Dialog>
  );
};

export default AddContactForm;
